import type { Cents } from "./money";
import type { CostType } from "./proposedCostLine";
import type { FinancialBucket } from "./buckets";
import type { QuantityUnit } from "./feature";

/**
 * Purchases (Phase 5) — real-world financial transactions.
 *
 *   Purchase (NO project — a receipt/invoice may span many projects)
 *     └── Purchase Line (0..n; a single catch-all line is normal)
 *           └── Allocation (0..n) → project + optional feature XOR proposed line
 *
 * Only ALLOCATIONS place purchase money on a project's books; the engine
 * never counts an entire purchase against a project. Money is
 * authoritative on allocations; quantity is documentary.
 *
 * Returns/refunds are Purchases with kind='return', negative amounts,
 * linked to the original purchase (and lines to original lines where
 * known). The original is never modified.
 *
 * Header reconciliation (validated on save):
 *   total = subtotal − discount + tax + shipping
 * Distribution of purchase-level discount, tax, and shipping is done by
 * the deterministic engine (src/engine/purchaseMath.ts) with
 * largest-remainder rounding so cents always reconcile exactly.
 */

export type PurchaseKind = "purchase" | "return";
export type PurchaseStatus = "active" | "voided";

export type RefundMethod = "original-payment" | "cash" | "store-credit" | "exchange" | "unknown" | "other";

export const REFUND_METHODS: ReadonlyArray<{ id: RefundMethod; displayName: string }> = [
  { id: "original-payment", displayName: "Original payment method" },
  { id: "cash", displayName: "Cash" },
  { id: "store-credit", displayName: "Store credit" },
  { id: "exchange", displayName: "Exchange — no cash confirmed" },
  { id: "unknown", displayName: "Unknown — no cash confirmed" },
  { id: "other", displayName: "Other" },
];

export interface Purchase {
  id: string;
  kind: PurchaseKind;
  /** For returns: the purchase being returned against. */
  originalPurchaseId: string | null;
  vendor: string;
  /** ISO date. */
  date: string;
  receiptNumber: string;
  /** Σ line gross amounts as printed on the receipt (authoritative). */
  subtotalCents: Cents;
  /** Purchase-level discount (coupon, Pro discount, promo credit). */
  discountCents: Cents;
  /** Sales tax as printed on the receipt (authoritative). */
  taxCents: Cents;
  shippingCents: Cents;
  /** subtotal − discount + tax + shipping. */
  totalCents: Cents;
  paymentMethod: string;
  refundMethod: RefundMethod | null;
  notes: string;
  status: PurchaseStatus;
  voidedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface PurchaseLine {
  id: string;
  purchaseId: string;
  /** For return lines: the original purchase line being returned. */
  originalLineId: string | null;
  description: string;
  quantityX100: number | null;
  unit: QuantityUnit | null;
  sku: string;
  /** Gross line amount before any discount (pre-tax). */
  grossCents: Cents;
  /** Line-specific discount. */
  lineDiscountCents: Cents;
  taxable: boolean;
  /** Explicit per-line tax from the receipt when available; null = derive. */
  explicitTaxCents: Cents | null;
  /** Catalog product this line purchased (classification/procurement
   *  provenance only — NEVER the source of purchase amounts). */
  productId: string | null;
  /** Default classification for allocations of this line. */
  categoryId: string | null;
  costType: CostType | null;
  notes: string;
  sortOrder: number;
}

export interface PurchaseAllocation {
  id: string;
  lineId: string;
  projectId: string;
  /** Authoritative allocated pre-tax net amount (negative on returns). */
  amountCents: Cents;
  /** Documentary only. */
  quantityX100: number | null;
  /** Explicit shipping assignment; null = proportional share. */
  shippingOverrideCents: Cents | null;
  /** Explicit allocation tax; null/omitted retains legacy proportional math. */
  taxOverrideCents?: Cents | null;
  categoryOverrideId: string | null;
  bucketOverride: FinancialBucket | null;
  linkedProposedLineId: string | null;
  linkedFeatureId: string | null;
  notes: string;
  createdAt: string;
  updatedAt: string;
}

/** A purchase with its full graph — the unit the engine reasons about. */
export interface PurchaseGraph {
  /** Explicitly scoped, non-persisted collaborator view; never a source transaction. */
  projectPortionOnly?: boolean;
  purchase: Purchase;
  lines: PurchaseLine[];
  allocations: PurchaseAllocation[];
}

export interface PurchaseHeaderInput {
  kind: PurchaseKind;
  originalPurchaseId: string | null;
  vendor: string;
  date: string;
  receiptNumber: string;
  subtotalCents: Cents;
  discountCents: Cents;
  taxCents: Cents;
  shippingCents: Cents;
  totalCents: Cents;
  paymentMethod: string;
  refundMethod: RefundMethod | null;
  notes: string;
}

export interface PurchaseLineInput {
  originalLineId?: string | null;
  /** Optional catalog product link (classification/procurement only). */
  productId?: string | null;
  description: string;
  quantityX100: number | null;
  unit: QuantityUnit | null;
  sku: string;
  grossCents: Cents;
  lineDiscountCents: Cents;
  taxable: boolean;
  explicitTaxCents: Cents | null;
  categoryId: string | null;
  costType: CostType | null;
  notes: string;
}

export interface PurchaseAllocationInput {
  projectId: string;
  amountCents: Cents;
  quantityX100: number | null;
  shippingOverrideCents: Cents | null;
  /** Explicit allocation tax; null/omitted retains legacy proportional math. */
  taxOverrideCents?: Cents | null;
  categoryOverrideId: string | null;
  bucketOverride: FinancialBucket | null;
  linkedProposedLineId: string | null;
  linkedFeatureId: string | null;
  notes: string;
}

function isCents(v: unknown): v is number {
  return typeof v === "number" && Number.isSafeInteger(v);
}

/** Sign rule: purchases carry non-negative money; returns non-positive. */
function signOk(kind: PurchaseKind, cents: number): boolean {
  return kind === "purchase" ? cents >= 0 : cents <= 0;
}

export function validatePurchaseHeader(input: PurchaseHeaderInput): string[] {
  const problems: string[] = [];
  if (input.vendor.trim() === "") problems.push("Vendor is required.");
  if (input.date.trim() === "") problems.push("Date is required.");
  for (const [label, v] of [
    ["Subtotal", input.subtotalCents],
    ["Discount", input.discountCents],
    ["Tax", input.taxCents],
    ["Shipping", input.shippingCents],
    ["Total", input.totalCents],
  ] as const) {
    if (!isCents(v)) problems.push(`${label} must be a valid amount.`);
    else if (!signOk(input.kind, v)) {
      problems.push(
        input.kind === "purchase"
          ? `${label} cannot be negative on a purchase.`
          : `${label} must be entered as a return (non-positive) amount.`,
      );
    }
  }
  if (problems.length === 0) {
    const expected =
      input.subtotalCents - input.discountCents + input.taxCents + input.shippingCents;
    if (expected !== input.totalCents) {
      problems.push(
        "Total must equal Subtotal − Discount + Tax + Shipping so the receipt reconciles.",
      );
    }
  }
  if (input.kind === "return" && input.originalPurchaseId === null) {
    problems.push("A return must reference the original purchase.");
  }
  return problems;
}

export function validatePurchaseLine(kind: PurchaseKind, input: PurchaseLineInput): string[] {
  const problems: string[] = [];
  if (input.description.trim() === "") problems.push("Line description is required.");
  if (!isCents(input.grossCents)) problems.push("Line amount must be a valid amount.");
  else if (!signOk(kind, input.grossCents)) problems.push("Line amount sign must match the purchase kind.");
  if (!isCents(input.lineDiscountCents)) problems.push("Line discount must be a valid amount.");
  else if (!signOk(kind, input.lineDiscountCents)) problems.push("Line discount sign must match the purchase kind.");
  if (input.explicitTaxCents !== null && !isCents(input.explicitTaxCents)) {
    problems.push("Line tax must be a valid amount.");
  }
  // Quantity carries the SAME sign as the money (Phase 9D): a return line
  // takes units back, so its quantity is negative and procurement nets
  // down. Mixing the signs would silently add returned goods to stock.
  if (input.quantityX100 !== null && (!isCents(input.quantityX100) || !signOk(kind, input.quantityX100))) {
    problems.push(
      kind === "return"
        ? "Return quantity must be entered as a negative (returned) quantity."
        : "Quantity must be a valid non-negative number.",
    );
  }
  if (isCents(input.grossCents) && isCents(input.lineDiscountCents) &&
      Math.abs(input.lineDiscountCents) > Math.abs(input.grossCents)) {
    problems.push("Line discount cannot exceed the line amount.");
  }
  return problems;
}

export function validateAllocation(input: PurchaseAllocationInput): string[] {
  const problems: string[] = [];
  if(input.taxOverrideCents!=null&&!isCents(input.taxOverrideCents))problems.push("Tax override must be valid integer cents.");
  if (input.projectId.trim() === "") problems.push("Allocation project is required.");
  if (!isCents(input.amountCents)) problems.push("Allocation amount must be a valid amount.");
  if (input.shippingOverrideCents !== null && !isCents(input.shippingOverrideCents)) {
    problems.push("Shipping override must be a valid amount.");
  }
  if (input.linkedProposedLineId != null && input.linkedFeatureId != null) {
    problems.push("An allocation can link to a proposed item OR a feature, not both.");
  }
  return problems;
}
